import { supabaseClient } from './supabase';
import { supabaseLocationService } from './supabaseLocationService';

// Chart.js type imports for data structures
import type { ChartData, ChartOptions, TooltipItem } from 'chart.js';

// Data interfaces for each table
export interface EducationalAttainmentRow {
  location_name: string;
  year: number;
  race: string;
  gender: string;
  educ_level: string;
  percent: number;
  count?: number;
}

export interface HealthInsuranceRow {
  location_name: string;
  year: number;
  percent_no_insurance: number;
  margin_of_error: number;
  county?: string;
  state?: string;
  geography_type?: string;
}

export interface EVTractRow {
  tract: number;
  County: string;
  total_vehicles: number;
  evs_per_100_people: number;
  total_population: number;
  median_income: number;
  pct_bachelor_or_higher: number;
  num_level2: number;
  num_dc_fast: number;
  total_chargers: number;
  pct_owner_occupied: number;
  vehicles_per_household: number;
}

export interface PvSizeRow {
  location_name: string;
  year: number;
  sector: string;
  total_watts: number;
  geoid: number;
  project_count: number;
}

export interface PopulationRow {
  location_name: string;
  county: string;
  population: number;
  year: number;
  geofips?: number;
  geography_type?: string;
}

// Combined data structures for both geographic resolutions
export interface CityLevelEquityData {
  locationName: string;
  county: string;
  population: number;
  educationPercent: number;        // % with Bachelor's or higher
  healthInsurancePercent: number;  // % with health insurance (100 - % no insurance)
  evsPer1000: number;             // EVs per 1000 residents (aggregated from tracts)
  solarKwPerCapita: number;       // Solar kW per capita
  geofips?: number;
  year: number;
}

export interface TractLevelEquityData {
  tract: number;
  county: string;
  population: number;
  educationPercent: number;        // % with Bachelor's or higher (from tract data)
  healthInsurancePercent: number;  // % with health insurance (estimated from city/county)
  evsPer1000: number;             // EVs per 1000 residents
  solarKwPerCapita: number;       // Solar kW per capita (estimated from city)
  medianIncome: number;
  year: number;
}

export type EquityDataResolution = 'city' | 'tract';
export type EquityChartType = 'bubble' | 'scatter' | 'heatmap';

// Chart.js specific interfaces for equity data visualization
export interface EquityBubbleChartDataPoint {
  x: number;        // Education percentage
  y: number;        // Solar kW per capita
  r: number;        // EV adoption (bubble radius)
  insurance: number; // Health insurance percentage (for color)
  location: string;  // Location name for tooltip
  population: number; // Population for tooltip
}

export interface EquityScatterChartDataPoint {
  x: number;        // Education percentage
  y: number;        // Solar kW per capita
  insurance: number; // Health insurance percentage (for color)
  evs: number;      // EV adoption for tooltip
  location: string;  // Location name for tooltip
}

export interface EquityHeatmapDataPoint {
  x: number;        // Education bin
  y: number;        // Solar bin
  v: number;        // EV adoption intensity (value)
  locations: number; // Number of locations in this bin
}

export interface DataRanges {
  education: { min: number; max: number };
  solar: { min: number; max: number };
  evs: { min: number; max: number };
  insurance: { min: number; max: number };
}

// Standard color schemes following app patterns
export const EQUITY_COLOR_SCHEMES = {
  insurance: {
    light: '#3b82f6',      // Light blue
    medium: '#8b5cf6',     // Medium purple
    dark: '#9333ea'        // Dark purple
  },
  education: {
    light: '#10b981',      // Light green
    medium: '#059669',     // Medium green
    dark: '#047857'        // Dark green
  },
  evs: {
    light: '#f59e0b',      // Light amber
    medium: '#d97706',     // Medium amber
    dark: '#b45309'        // Dark amber
  },
  solar: {
    light: '#dc2626',      // Light red
    medium: '#b91c1c',     // Medium red
    dark: '#991b1b'        // Dark red
  },
  neutral: {
    light: '#9ca3af',      // Gray-400
    medium: '#6b7280',     // Gray-500
    dark: '#374151'        // Gray-700
  }
} as const;

export type EquityDataPoint = CityLevelEquityData | TractLevelEquityData;

/**
 * Service for fetching and combining climate equity data from multiple Supabase tables
 */
export class ClimateEquityDataService {

  /**
   * Get climate equity data for a specific location at both city and tract level
   */
  static async getEquityData(
    locationName: string,
    resolution: EquityDataResolution = 'city',
    year: number = 2023
  ): Promise<CityLevelEquityData[] | TractLevelEquityData[]> {
    try {

      if (resolution === 'city') {
        return await this.getCityLevelEquityData(locationName, year);
      } else {
        return await this.getTractLevelEquityData(locationName, year);
      }
    } catch (error) {
      console.error('Error fetching equity data:', error);
      throw new Error(`Failed to fetch equity data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get city-level aggregated equity data
   */
  private static async getCityLevelEquityData(locationName: string, year: number): Promise<CityLevelEquityData[]> {
    // Initialize location service
    await supabaseLocationService.initialize();
    const county = supabaseLocationService.getCountyForCity(locationName);

    if (!county) {
      throw new Error(`Could not determine county for ${locationName}`);
    }

    // Get geofips for targeted querying
    const geofips = await supabaseLocationService.getGeofips(locationName) || undefined;

    // Fetch all required data in parallel (with geofips filtering per requirements)
    const [
      educationData,
      healthData,
      populationData,
      pvData,
      evData
    ] = await Promise.all([
      this.getEducationData(locationName, year, geofips),
      this.getHealthInsuranceData(locationName, year, geofips),
      this.getPopulationData(locationName, year, geofips),
      this.getPvData(locationName, year, geofips),
      this.getEVDataForCity(county, year)
    ]);

    // Combine data into city-level records
    const cityData: CityLevelEquityData = {
      locationName,
      county,
      population: populationData?.population || 0,
      educationPercent: this.calculateEducationPercent(educationData),
      healthInsurancePercent: this.calculateHealthInsurancePercent(healthData),
      evsPer1000: this.calculateCityEVsPer1000(evData),
      solarKwPerCapita: this.calculateSolarPerCapita(pvData, populationData?.population || 0),
      geofips: populationData?.geofips,
      year
    };

    return [cityData];
  }

  /**
   * Get tract-level equity data
   */
  private static async getTractLevelEquityData(locationName: string, year: number): Promise<TractLevelEquityData[]> {
    // Initialize location service
    await supabaseLocationService.initialize();
    const county = supabaseLocationService.getCountyForCity(locationName);

    if (!county) {
      // If no county found, try some common variations
      console.warn(`Could not determine county for ${locationName}, trying fallback to city-level data`);
      // Fallback to city-level data converted to tract format
      const cityData = await this.getCityLevelEquityData(locationName, year);
      if (cityData.length > 0) {
        // Convert city data to tract format for consistency
        return [{
          tract: 0,
          county: cityData[0].county,
          population: cityData[0].population,
          educationPercent: cityData[0].educationPercent,
          healthInsurancePercent: cityData[0].healthInsurancePercent,
          evsPer1000: cityData[0].evsPer1000,
          solarKwPerCapita: cityData[0].solarKwPerCapita,
          medianIncome: 50000, // Default estimate
          year
        }];
      }
      throw new Error(`Could not determine county for ${locationName}`);
    }

    // Get tract-level EV data for the county
    const evTracts = await this.getEVTractData(county);

    if (evTracts.length === 0) {
      console.warn(`No tract data found for county: ${county}, falling back to city-level data`);
      // Fallback to city-level data
      const cityData = await this.getCityLevelEquityData(locationName, year);
      if (cityData.length > 0) {
        return [{
          tract: 0,
          county: cityData[0].county,
          population: cityData[0].population,
          educationPercent: cityData[0].educationPercent,
          healthInsurancePercent: cityData[0].healthInsurancePercent,
          evsPer1000: cityData[0].evsPer1000,
          solarKwPerCapita: cityData[0].solarKwPerCapita,
          medianIncome: 50000, // Default estimate
          year
        }];
      }
      throw new Error(`No tract data found for county: ${county}`);
    }

    // Get county-level solar data for realistic tract-level distribution
    const [healthData, countyPvData] = await Promise.all([
      this.getHealthInsuranceData(locationName, year),
      this.getCountyPvData(county, year)
    ]);

    const cityHealthInsurancePercent = this.calculateHealthInsurancePercent(healthData);
    const countySolarPerCapita = this.calculateCountySolarPerCapita(countyPvData);


    // Convert tract data to equity format with demographic-based solar distribution
    const tractData: TractLevelEquityData[] = evTracts.map(tract => ({
      tract: tract.tract,
      county: tract.County,
      population: tract.total_population || 0,
      educationPercent: (tract.pct_bachelor_or_higher || 0) * 100,
      healthInsurancePercent: cityHealthInsurancePercent, // Estimated from city level
      evsPer1000: (tract.evs_per_100_people || 0) * 10, // Convert from per 100 to per 1000
      solarKwPerCapita: this.estimateTractSolarFromCounty(countySolarPerCapita, tract),
      medianIncome: tract.median_income || 0,
      year
    }));

    return tractData;
  }

  /**
   * Fetch education data for a location (filtered by geofips first per requirements)
   */
  private static async getEducationData(locationName: string, year: number, geofips?: number): Promise<EducationalAttainmentRow[]> {
    let query = supabaseClient
      .from('educational_attain_race')
      .select('*')
      .eq('year', year)
      .eq('educ_level', "Bachelor's degree or higher")
      .eq('gender', 'Total'); // Use total gender to avoid double counting

    // Filter by geofips first if available (per requirements)
    if (geofips) {
      // Note: This table might not have geofips, so we fallback to location name
    }

    // Always filter by location name to prevent downloading large datasets
    query = query.ilike('location_name', `%${locationName}%`).limit(50);

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching education data:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Fetch health insurance data for a location (filtered by geofips first per requirements)
   */
  private static async getHealthInsuranceData(locationName: string, year: number, geofips?: number): Promise<HealthInsuranceRow[]> {
    let query = supabaseClient
      .from('percent_no_health_insure')
      .select('*')
      .eq('year', year);

    // Filter by geofips first if available (per requirements)
    if (geofips) {
      // This table might not have geofips, so we fallback to location name
    }

    // Always filter by location name to prevent downloading large datasets
    query = query.ilike('location_name', `%${locationName}%`).limit(10);

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching health insurance data:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Fetch population data for a location (filtered by geofips first per requirements)
   */
  private static async getPopulationData(locationName: string, year: number, geofips?: number): Promise<PopulationRow | null> {
    let query = supabaseClient
      .from('population')
      .select('*')
      .eq('year', year);

    // Filter by geofips first if available (per requirements)
    if (geofips) {
      query = query.eq('geofips', geofips);
    } else {
      // Fallback to location name
      query = query.ilike('location_name', `%${locationName}%`);
    }

    const { data, error } = await query.limit(1).single();

    if (error) {
      console.error('Error fetching population data:', error);
      return null;
    }

    return data;
  }

  /**
   * Fetch PV solar data for a location (filtered by geoid first per requirements)
   */
  private static async getPvData(locationName: string, year: number, geofips?: number): Promise<PvSizeRow[]> {
    let query = supabaseClient
      .from('all_pv_size')
      .select('*')
      .eq('year', year);

    // Filter by geoid first if available (per requirements)
    if (geofips) {
      query = query.eq('geoid', geofips);
    } else {
      // Fallback to location name
      query = query.ilike('location_name', `%${locationName}%`);
    }

    const { data, error } = await query.limit(50);

    if (error) {
      console.error('Error fetching PV data:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Fetch tract-level EV data for a county
   */
  private static async getEVTractData(county: string): Promise<EVTractRow[]> {
    // Try exact match first
    let { data, error } = await supabaseClient
      .from('ev_by_tract_2023')
      .select('*')
      .eq('County', county)
      .not('total_population', 'is', null)
      .not('evs_per_100_people', 'is', null)
      .limit(1000);

    if (error) {
      console.error('Error fetching EV tract data:', error);
      throw error;
    }

    // If no exact match, try fuzzy matching
    if (!data || data.length === 0) {

      const result = await supabaseClient
        .from('ev_by_tract_2023')
        .select('*')
        .ilike('County', `%${county.replace(' County', '').replace(' Co.', '')}%`)
        .not('total_population', 'is', null)
        .not('evs_per_100_people', 'is', null)
        .limit(1000);

      if (result.error) {
        console.error('Error fetching EV tract data (fuzzy):', result.error);
        throw result.error;
      }

      data = result.data;
    }

    return data || [];
  }

  /**
   * Get aggregated EV data for a city from tract-level data
   */
  private static async getEVDataForCity(county: string, year: number): Promise<EVTractRow[]> {
    return await this.getEVTractData(county);
  }

  /**
   * Fetch county-level PV solar data for tract-level distribution
   */
  private static async getCountyPvData(county: string, year: number): Promise<PvSizeRow[]> {
    // Try to get county-level data by county name
    let query = supabaseClient
      .from('all_pv_size')
      .select('*')
      .eq('year', year)
      .ilike('location_name', `%${county.replace(' County', '').replace(' Co.', '')}%`)
      .eq('geography', 'County');

    let { data, error } = await query.limit(10);

    if (error) {
      console.error('Error fetching county PV data:', error);
      throw error;
    }

    // If no county-level data, try to get all cities in the county and aggregate
    if (!data || data.length === 0) {

      query = supabaseClient
        .from('all_pv_size')
        .select('*')
        .eq('year', year)
        .ilike('county', `%${county.replace(' County', '').replace(' Co.', '')}%`)
        .eq('geography', 'City');

      const result = await query.limit(50);

      if (result.error) {
        console.error('Error fetching city PV data for county:', result.error);
        throw result.error;
      }

      data = result.data;
    }

    return data || [];
  }

  /**
   * Calculate county-level solar kW per capita for tract distribution
   */
  private static calculateCountySolarPerCapita(countyPvData: PvSizeRow[]): number {
    if (countyPvData.length === 0) {
      return 0;
    }

    // Sum all county/city solar installations
    const totalKw = countyPvData.reduce((sum, row) => {
      return sum + ((row.total_watts || 0) / 1000);
    }, 0);

    // Use a base population estimate for county (will be adjusted per tract)
    const basePopulation = 500000; // Average county population
    const result = totalKw / basePopulation;


    return result;
  }

  /**
   * Estimate tract-level solar from county data using demographic factors
   */
  private static estimateTractSolarFromCounty(countySolarPerCapita: number, tract: EVTractRow): number {
    if (countySolarPerCapita === 0) return 0;

    // Create realistic variation based on tract characteristics
    // Solar adoption correlates with: income, education, homeownership, vehicle ownership

    // Normalize tract characteristics to 0-1 scale
    const educationFactor = Math.min(1, Math.max(0, (tract.pct_bachelor_or_higher || 0.3))); // 0-1 scale
    const incomeFactor = Math.min(1, Math.max(0.2, (tract.median_income || 50000) / 100000)); // 0.2-1 scale
    const homeownershipFactor = Math.min(1, Math.max(0.3, (tract.pct_owner_occupied || 0.6))); // 0.3-1 scale
    const vehicleFactor = Math.min(1, Math.max(0.5, (tract.vehicles_per_household || 1.5) / 3)); // 0.5-1 scale

    // Weighted combination of factors (education and income are strongest predictors)
    const demographicMultiplier = (
      educationFactor * 0.35 +
      incomeFactor * 0.35 +
      homeownershipFactor * 0.20 +
      vehicleFactor * 0.10
    );

    // Apply multiplier with realistic bounds (0.3x to 2.5x county average)
    const tractSolar = countySolarPerCapita * (0.3 + demographicMultiplier * 2.2);

    return tractSolar;
  }

  /**
   * Calculate education percentage from education data
   */
  private static calculateEducationPercent(educationData: EducationalAttainmentRow[]): number {
    if (educationData.length === 0) return 0;

    // Calculate weighted average across all races
    let totalPercent = 0;
    let validRecords = 0;

    educationData.forEach(row => {
      if (row.percent !== null && row.percent !== undefined) {
        totalPercent += row.percent * 100; // Convert decimal to percentage
        validRecords++;
      }
    });

    return validRecords > 0 ? totalPercent / validRecords : 0;
  }

  /**
   * Calculate health insurance coverage percentage
   */
  private static calculateHealthInsurancePercent(healthData: HealthInsuranceRow[]): number {
    if (healthData.length === 0) return 0;

    // Use the most recent data point
    const latestData = healthData[healthData.length - 1];
    return 100 - (latestData.percent_no_insurance || 0);
  }

  /**
   * Calculate city-level EVs per 1000 from tract data
   */
  private static calculateCityEVsPer1000(evData: EVTractRow[]): number {
    if (evData.length === 0) return 0;

    // Calculate population-weighted average
    let totalEVs = 0;
    let totalPopulation = 0;

    evData.forEach(tract => {
      if (tract.total_population && tract.evs_per_100_people) {
        const tractEVs = (tract.evs_per_100_people / 100) * tract.total_population;
        totalEVs += tractEVs;
        totalPopulation += tract.total_population;
      }
    });

    return totalPopulation > 0 ? (totalEVs / totalPopulation) * 1000 : 0;
  }

  /**
   * Calculate solar kW per capita
   */
  private static calculateSolarPerCapita(pvData: PvSizeRow[], population: number): number {
    if (pvData.length === 0 || population === 0) {
      return 0;
    }

    // Sum all sectors and convert watts to kW
    const totalKw = pvData.reduce((sum, row) => {
      return sum + ((row.total_watts || 0) / 1000);
    }, 0);

    const result = totalKw / population;

    return result;
  }

  /**
   * Get available locations for equity analysis
   */
  static async getAvailableLocations(): Promise<string[]> {
    try {
      const { data, error } = await supabaseClient
        .from('population')
        .select('location_name')
        .eq('year', 2023)
        .order('location_name');

      if (error) throw error;

      return Array.from(new Set(data?.map(row => row.location_name) || []));
    } catch (error) {
      console.error('Error fetching available locations:', error);
      return [];
    }
  }

  /**
   * Get available counties in EV tract data (for debugging)
   */
  static async getAvailableCounties(): Promise<string[]> {
    try {
      const { data, error } = await supabaseClient
        .from('ev_by_tract_2023')
        .select('County')
        .limit(1000);

      if (error) throw error;

      const counties = Array.from(new Set(data?.map(row => row.County) || []));
      return counties;
    } catch (error) {
      console.error('Error fetching available counties:', error);
      return [];
    }
  }

  /**
   * Calculate correlation coefficient between two data series
   */
  static calculateCorrelation(x: number[], y: number[]): number {
    if (x.length !== y.length || x.length === 0) return 0;

    const n = x.length;
    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i], 0);
    const sumX2 = x.reduce((sum, xi) => sum + xi * xi, 0);
    const sumY2 = y.reduce((sum, yi) => sum + yi * yi, 0);

    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

    return denominator === 0 ? 0 : numerator / denominator;
  }

  /**
   * Get summary statistics for a dataset
   */
  static getDatasetStats(data: CityLevelEquityData[] | TractLevelEquityData[]) {
    if (data.length === 0) {
      return {
        totalLocations: 0,
        avgEducation: 0,
        avgInsurance: 0,
        avgEVs: 0,
        avgSolar: 0,
        correlationEducationEVs: 0,
        correlationInsuranceEVs: 0
      };
    }

    const educationValues = data.map(d => d.educationPercent);
    const insuranceValues = data.map(d => d.healthInsurancePercent);
    const evValues = data.map(d => d.evsPer1000);
    const solarValues = data.map(d => d.solarKwPerCapita);

    return {
      totalLocations: data.length,
      avgEducation: educationValues.reduce((a, b) => a + b, 0) / data.length,
      avgInsurance: insuranceValues.reduce((a, b) => a + b, 0) / data.length,
      avgEVs: evValues.reduce((a, b) => a + b, 0) / data.length,
      avgSolar: solarValues.reduce((a, b) => a + b, 0) / data.length,
      correlationEducationEVs: this.calculateCorrelation(educationValues, evValues),
      correlationInsuranceEVs: this.calculateCorrelation(insuranceValues, evValues)
    };
  }

  // ==================== CHART.JS INTEGRATION METHODS ====================

  /**
   * Calculate data ranges for visualization scaling (moved from component)
   */
  static calculateDataRanges(data: EquityDataPoint[]): DataRanges {
    if (data.length === 0) {
      return {
        education: { min: 0, max: 100 },
        solar: { min: 0, max: 1 },
        evs: { min: 0, max: 100 },
        insurance: { min: 0, max: 100 }
      };
    }

    const educationValues = data.map(d => d.educationPercent);
    const solarValues = data.map(d => d.solarKwPerCapita);
    const evValues = data.map(d => d.evsPer1000);
    const insuranceValues = data.map(d => d.healthInsurancePercent);

    return {
      education: {
        min: Math.min(...educationValues),
        max: Math.max(...educationValues)
      },
      solar: {
        min: Math.min(...solarValues),
        max: Math.max(...solarValues)
      },
      evs: {
        min: Math.min(...evValues),
        max: Math.max(...evValues)
      },
      insurance: {
        min: Math.min(...insuranceValues),
        max: Math.max(...insuranceValues)
      }
    };
  }

  /**
   * Get color for a value within a range using predefined color schemes
   */
  static getColorForValue(
    value: number,
    min: number,
    max: number,
    scheme: keyof typeof EQUITY_COLOR_SCHEMES = 'insurance'
  ): string {
    if (max === min) return EQUITY_COLOR_SCHEMES[scheme].medium;

    const normalized = Math.max(0, Math.min(1, (value - min) / (max - min)));
    const colors = EQUITY_COLOR_SCHEMES[scheme];

    // Interpolate between light and dark based on normalized value
    if (normalized < 0.5) {
      // Interpolate between light and medium
      const ratio = normalized * 2; // 0-1
      return this.interpolateColor(colors.light, colors.medium, ratio);
    } else {
      // Interpolate between medium and dark
      const ratio = (normalized - 0.5) * 2; // 0-1
      return this.interpolateColor(colors.medium, colors.dark, ratio);
    }
  }

  /**
   * Interpolate between two hex colors
   */
  private static interpolateColor(color1: string, color2: string, ratio: number): string {
    const hex = (color: string) => parseInt(color.slice(1), 16);
    const r1 = (hex(color1) >> 16) & 255;
    const g1 = (hex(color1) >> 8) & 255;
    const b1 = hex(color1) & 255;

    const r2 = (hex(color2) >> 16) & 255;
    const g2 = (hex(color2) >> 8) & 255;
    const b2 = hex(color2) & 255;

    const r = Math.round(r1 + (r2 - r1) * ratio);
    const g = Math.round(g1 + (g2 - g1) * ratio);
    const b = Math.round(b1 + (b2 - b1) * ratio);

    return `rgb(${r}, ${g}, ${b})`;
  }

  /**
   * Transform equity data for Chart.js bubble chart
   */
  static getBubbleChartData(data: EquityDataPoint[]): ChartData<'bubble'> {
    const ranges = this.calculateDataRanges(data);

    const bubbleData: EquityBubbleChartDataPoint[] = data.map(point => {
      const evNormalized = ranges.evs.max > ranges.evs.min
        ? (point.evsPer1000 - ranges.evs.min) / (ranges.evs.max - ranges.evs.min)
        : 0.5;
      const radius = 6 + evNormalized * 19; // 6-25px radius range

      const locationName = 'locationName' in point ? point.locationName : `Tract ${point.tract}`;

      return {
        x: point.educationPercent,
        y: point.solarKwPerCapita,
        r: radius,
        insurance: point.healthInsurancePercent,
        location: locationName,
        population: point.population
      };
    });

    return {
      datasets: [
        {
          label: 'Communities',
          data: bubbleData.map(point => ({
            x: point.x,
            y: point.y,
            r: point.r
          })),
          backgroundColor: bubbleData.map(point =>
            this.getColorForValue(point.insurance, ranges.insurance.min, ranges.insurance.max, 'insurance')
          ),
          borderColor: EQUITY_COLOR_SCHEMES.neutral.dark,
          borderWidth: 1.5,
          hoverBorderWidth: 2
        }
      ]
    };
  }

  /**
   * Transform equity data for Chart.js scatter chart
   */
  static getScatterChartData(data: EquityDataPoint[]): ChartData<'scatter'> {
    const ranges = this.calculateDataRanges(data);

    const scatterData: EquityScatterChartDataPoint[] = data.map(point => {
      const locationName = 'locationName' in point ? point.locationName : `Tract ${point.tract}`;

      return {
        x: point.educationPercent,
        y: point.solarKwPerCapita,
        insurance: point.healthInsurancePercent,
        evs: point.evsPer1000,
        location: locationName
      };
    });

    return {
      datasets: [
        {
          label: 'Communities',
          data: scatterData.map(point => ({
            x: point.x,
            y: point.y
          })),
          backgroundColor: scatterData.map(point =>
            this.getColorForValue(point.insurance, ranges.insurance.min, ranges.insurance.max, 'insurance')
          ),
          borderColor: EQUITY_COLOR_SCHEMES.neutral.dark,
          pointRadius: 5,
          pointHoverRadius: 7,
          borderWidth: 1
        }
      ]
    };
  }

  /**
   * Transform equity data for Chart.js heatmap (using scatter with sized points)
   */
  static getHeatmapChartData(data: EquityDataPoint[]): ChartData<'scatter'> {
    const ranges = this.calculateDataRanges(data);

    // Create grid bins for heatmap
    const gridSize = 10;
    const heatmapData: Map<string, EquityHeatmapDataPoint> = new Map();

    data.forEach(point => {
      const educationBin = Math.floor((point.educationPercent / 100) * gridSize);
      const solarBin = Math.floor(((point.solarKwPerCapita - ranges.solar.min) / (ranges.solar.max - ranges.solar.min)) * gridSize);
      const key = `${educationBin}-${solarBin}`;

      if (!heatmapData.has(key)) {
        heatmapData.set(key, {
          x: (educationBin + 0.5) * (100 / gridSize), // Center of bin
          y: ranges.solar.min + (solarBin + 0.5) * (ranges.solar.max - ranges.solar.min) / gridSize,
          v: 0,
          locations: 0
        });
      }

      const bin = heatmapData.get(key)!;
      bin.v += point.evsPer1000;
      bin.locations += 1;
    });

    // Calculate average EV adoption for each bin
    const processedData = Array.from(heatmapData.values()).map(bin => ({
      ...bin,
      v: bin.v / bin.locations // Average EV adoption
    })).filter(bin => bin.locations > 0);

    if (processedData.length === 0) {
      return {
        datasets: [
          {
            label: 'No Data',
            data: [],
            backgroundColor: [],
            pointRadius: []
          }
        ]
      };
    }

    const evRange = {
      min: Math.min(...processedData.map(d => d.v)),
      max: Math.max(...processedData.map(d => d.v))
    };

    return {
      datasets: [
        {
          label: 'EV Adoption Intensity',
          data: processedData.map(point => ({
            x: point.x,
            y: point.y
          })),
          backgroundColor: processedData.map(point => {
            const intensity = evRange.max > evRange.min
              ? (point.v - evRange.min) / (evRange.max - evRange.min)
              : 0.5;
            const alpha = 0.1 + intensity * 0.9;
            return `rgba(220, 38, 38, ${alpha})`; // Red with varying opacity
          }),
          borderColor: EQUITY_COLOR_SCHEMES.neutral.medium,
          pointRadius: processedData.map(() => 15), // Fixed size for heatmap squares
          borderWidth: 0.5
        }
      ]
    };
  }

  /**
   * Generate Chart.js options for equity charts
   */
  static getChartOptions(
    chartType: EquityChartType,
    data: EquityDataPoint[],
    resolution: EquityDataResolution = 'city'
  ): ChartOptions<'bubble' | 'scatter'> {

    const baseOptions: ChartOptions<'bubble' | 'scatter'> = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false, // Following app pattern
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 20,
            filter: (legendItem) => {
              // Only show legend for heatmap
              return chartType === 'heatmap';
            }
          }
        },
        tooltip: {
          callbacks: {
            title: () => '',
            label: (context: TooltipItem<'bubble' | 'scatter'>) => {
              const dataIndex = context.dataIndex;
              const point = data[dataIndex];
              if (!point) return '';

              const locationName = 'locationName' in point ? point.locationName : `Tract ${point.tract}`;
              const lines = [
                locationName,
                `Education: ${point.educationPercent.toFixed(1)}%`,
                `Solar kW/capita: ${point.solarKwPerCapita.toFixed(3)}`,
                `EVs/1000: ${point.evsPer1000.toFixed(1)}`,
                `Insurance: ${point.healthInsurancePercent.toFixed(1)}%`
              ];

              if (chartType === 'bubble') {
                lines[3] += ' (bubble size)';
              }
              lines[4] += ' (color)';

              return lines;
            }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Education Level (% Bachelor\'s or Higher)',
            font: { weight: 'bold' as const }
          },
          min: 0,
          max: 100,
          grid: { display: false }, // Following app pattern
          ticks: {
            callback: (value) => `${value}%`
          }
        },
        y: {
          title: {
            display: true,
            text: 'Average Solar per Capita (kW)',
            font: { weight: 'bold' as const }
          },
          beginAtZero: true,
          grid: { color: 'rgba(0, 0, 0, 0.1)' },
          ticks: {
            callback: (value) => typeof value === 'number' ? value.toFixed(3) : value
          }
        }
      }
    };

    // Chart-specific customizations
    if (chartType === 'heatmap') {
      baseOptions.plugins!.tooltip!.callbacks!.label = (context: TooltipItem<'bubble' | 'scatter'>) => {
        // Custom tooltip for heatmap
        return [
          'Aggregated Data Bin',
          `Education Range: ${(context.parsed.x ?? 0).toFixed(1)}% area`,
          `Solar Range: ${(context.parsed.y ?? 0).toFixed(3)} kW area`,
          'Intensity: Average EV adoption'
        ];
      };
    }

    return baseOptions;
  }
}